import React, { useState, useRef, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Logger from "../../logger";
import i18n from "../../localization/i18n";
import * as rk from "../../localization/resourceKeys";
import Loader from "../components/common/loader/loader";
import VerifyChallenge from "../components/retrieveUsername/verifyChallenge";
import Verify from "../components/retrieveUsername/verify";
import Confirmation from "../components/retrieveUsername/confirmation";
import { SessionContext } from "../../context/sessionContext";
import "./retrieveUsername.module.scss";
import { StepLayout } from "../components/common/steplayout/StepLayout";

type RetrieveUsernameStep =
  | "verify"
  | "verify-challenge"
  | "confirmation";


const MyVWRetrieveUsername: React.FC = () => {
  const { sessionDetails } = useContext(SessionContext);
  const ref = useRef<any>(null);
  const navigate = useNavigate();
  const country = sessionDetails?.country?.toLowerCase();
  const isVW = sessionDetails?.brand?.toLowerCase() === "vw";
  const isAudi = sessionDetails?.brand?.toLowerCase() === "audi";
  const isVWCanada = isVW && country === "canada";
  const [step, setStep] = useState<RetrieveUsernameStep>("verify");
  const [submitPending, setSubmitPending] = useState(false);
  const [username, setUsername] = useState<string>("");
  const [ssn, setSSN] = useState<string>("");
  const [partyId, setPartyId] = useState<number | undefined>(undefined);
  const [zipCode, setZipcode] = useState<string>("");
  const [dateOfBirth, setDateOfBirth] = useState<string>("");
  const [accountNumber, setAccountNumber] = useState<string>("");
  const [challengeQ, setChallengeQ] = useState<string>("");
  const [isContinueEnabled, setIsContinueEnabled] = useState<boolean>(false);

  useEffect(() => {
    if (step !== "verify-challenge") {
      document
        .getElementsByClassName("page-content")[0]
        ?.scrollTo(0, 0);
    }
  }, [step]);

  if (step === "verify") {
    return (
      <StepLayout
        title={
          <h3 className={`${isVWCanada ? "title-vwc" : ""}`}>
            {i18n.t(rk.TITLE_RETRIEVE_USERNAME)}
          </h3>
        }
        primaryAction={{
          label: i18n.t(rk.BUTTON_CANCEL),
          onClick: () => {
            Logger.info(
              "Exited Retrieve Username Flow in Step 1",
              "Location ==> /myVW/retrieveUsername",
              sessionDetails,
            );
            navigate("/myVW/login");
          },
        }}
        secondaryAction={{
          label: i18n.t(rk.BUTTON_CONTINUE),
          disabled: !isContinueEnabled,
          onClick: async () => {
            setSubmitPending(true);

            const verifyUser =
              await ref.current?.verifyUserAction();

            setSubmitPending(false);

            if (verifyUser) {
              Logger.info(
                "Clicked Continue in Retrieve Username Flow Step 1",
                "Location ==> /myVW/retrieveUsername",
                sessionDetails,
              );

              setStep("verify-challenge");
            }
          },
        }}
      >
        <Loader isLoading={submitPending} />

        <Verify
          ref={ref}
          setSSN={(value) => setSSN(value)}
          setAccountNumber={(value) => setAccountNumber(value)}
          setZipcode={(value) => setZipcode(value)}
          setPartyId={(value) => setPartyId(value)}
          setChallengeQ={(value) => setChallengeQ(value)}
          setDateOfBirth={(value) => setDateOfBirth(value)}
          setIsContinueEnabled={(value) => setIsContinueEnabled(value)}
        />
      </StepLayout>
    );
  }

  if (step === "verify-challenge") {
    return (
      <StepLayout
        title={<h3>{i18n.t(rk.TITLE_RETRIEVE_USERNAME)}</h3>}
        primaryAction={{
          label: i18n.t(rk.BUTTON_CANCEL),
          onClick: () => {
            Logger.info(
              "Exited Retrieve Username Flow in Step 2",
              "Location ==> /myVW/retrieveUsername",
              sessionDetails,
            );
            navigate("/myVW/login");
          },
        }}
        secondaryAction={{
          label: i18n.t(rk.BUTTON_CONTINUE),
          disabled: !isContinueEnabled,
          onClick: async () => {
            setSubmitPending(true);

            const canTakeAction =
              await ref.current?.verifyUserAction();

            setSubmitPending(false);

            if (canTakeAction) {
              Logger.info(
                "Clicked Continue in Retrieve Username Flow Step 2",
                "Location ==> /myVW/retrieveUsername",
                sessionDetails,
              );

              setStep("confirmation");
            }
          },
        }}
      >
        <Loader isLoading={submitPending} />

        <VerifyChallenge
          ref={ref}
          setUsername={(value: string) => setUsername(value)}
          setIsContinueEnabled={(value) => setIsContinueEnabled(value)}
          ssn={ssn}
          partyId={partyId}
          zipCode={zipCode}
          challengeQuestion={challengeQ}
          dateOfBirth={dateOfBirth}
          accountNumber={accountNumber}
        />
      </StepLayout>
    );
  }

  return (
    <StepLayout
      title={
        <h3 className={`${isVWCanada ? "title-vwc" : ""}`}>
          {isVWCanada
            ? i18n.t(rk.TITLE_RETRIEVE_USERNAME_VF)
            : i18n.t(rk.TITLE_RETRIEVE_USERNAME)}
        </h3>
      }
      secondaryAction={{
        label: isAudi
          ? i18n.t(rk.BUTTON_LOGIN)
          : i18n.t(rk.TITLE_LOGIN),
        onClick: () => {
          Logger.info(
            "Clicked login button",
            "Location ==> /myVW/retrieveUsername",
            sessionDetails,
          );
          navigate("/myVW/login");
        },
      }}
    >
      <Confirmation userName={username} />
    </StepLayout>
  );
};


export default MyVWRetrieveUsername;